'use memo';

import { useContext } from 'react';
import { AustralianState } from '@urbanx/agx.campaigns.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  SearchNow as SearchNowComponent,
  type CMAParams as SearchNowCMAParams,
  type ComparativeAddress,
} from '@urbanx/agx.ui-components2';
import { useAppSelector } from 'hooks/useAppSelector';
import { useSelectedCampaign } from 'hooks/useSelectedCampaign';
import useFormAddressSelector from 'selectors/useFormAddressSelector';
import useFormPropertyDetailsSelector from 'selectors/useFormPropertyDetailsSelector';
import {
  useFetchCMAPropertySearch,
  useFetchMoreCMAProperties,
} from 'store/cmaPropertySearch/cmaPropertySearchReducer';
import type { CMAParams } from 'store/cmaPropertySearch/types';
import { cleanStreetAddress } from 'utils/formatAddress';
import { getLatLongForAddress } from 'utils/getLatLong';
import useGetStructuredAddressForProperty from './useGetStructuredAddressForProperty';

const toAustralianState = (
  state?: string | null
): AustralianState | undefined => {
  if (!state) return undefined;

  return Object.values(AustralianState).includes(state as AustralianState)
    ? (state as AustralianState)
    : undefined;
};

const cleanComparableSearchAddress = (
  property?: {
    unitNumber?: string;
    streetAddress?: string;
    suburb?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  } | null
): string => {
  if (!property) return 'Address undefined';

  const { unitNumber, streetAddress, suburb, state, country, postalCode } =
    property;
  const streetAddressText =
    unitNumber && streetAddress
      ? `${unitNumber}/${streetAddress}`
      : unitNumber || streetAddress;

  return [streetAddressText, suburb, state, country, postalCode]
    .filter(value => value != null && value !== '')
    .join(' ');
};

interface SearchNowProps {
  id: string;
  validate?: boolean;
  onValueChanged: (value: { id: string; value: ComparativeAddress[] }) => void;
  defaultValue?: ComparativeAddress[];
}

const SearchNow = ({
  id,
  onValueChanged,
  defaultValue,
  validate = false,
}: SearchNowProps) => {
  const { userAccount } = useContext(AuthContext);
  const getStructuredAddressForProperty = useGetStructuredAddressForProperty();
  const { selectedCampaign } = useSelectedCampaign();
  const propertyDetails = useFormPropertyDetailsSelector();
  const propertyAddress = useFormAddressSelector();
  const selectedForm = useAppSelector(state => state.form.selectedForm);
  const availableForms = useAppSelector(state => state.forms.availableForms);
  const formRegion = availableForms.find(
    form => form.type === selectedForm?.formType
  )?.state;
  const { BackendPrice, EstimatedSellingPrice } = useAppSelector(
    state => state.form.formValues
  );
  const {
    cmaPropertyResults,
    page,
    totalPages,
    totalProperties,
    cmaPropertyResultsLoadingState,
    cmaPropertyResultsLoadingMore,
  } = useAppSelector(({ cmaPropertySearch }) => cmaPropertySearch);
  const fetchCMAPropertySearch = useFetchCMAPropertySearch();
  const fetchMoreCMAProperties = useFetchMoreCMAProperties();
  const campaignState = toAustralianState(formRegion);
  const manualComparableStateToSearch = campaignState ?? AustralianState.QLD;
  const handleFetchCMAPropertySearch = (params: SearchNowCMAParams) => {
    return fetchCMAPropertySearch(params as CMAParams);
  };

  return (
    <SearchNowComponent
      id={id}
      onValueChanged={onValueChanged}
      defaultValue={defaultValue}
      validate={validate}
      propertyAddressLabel={cleanStreetAddress(propertyAddress ?? null)}
      propertyAddressSearchText={cleanComparableSearchAddress(propertyAddress)}
      propertyDetails={propertyDetails}
      campaignState={campaignState}
      manualComparableStateToSearch={manualComparableStateToSearch}
      backendPrice={BackendPrice}
      estimatedSellingPrice={EstimatedSellingPrice}
      cmaPropertyResults={cmaPropertyResults}
      cmaPropertyResultsLoadingState={cmaPropertyResultsLoadingState}
      cmaPropertyResultsLoadingMore={cmaPropertyResultsLoadingMore}
      cmaPropertyPage={page}
      cmaPropertyTotalPages={totalPages}
      cmaPropertyTotalProperties={totalProperties}
      canSearch={!!userAccount}
      fetchCMAPropertySearch={handleFetchCMAPropertySearch}
      fetchMoreCMAProperties={fetchMoreCMAProperties}
      getLatLongForAddress={getLatLongForAddress}
      getStructuredAddressForProperty={getStructuredAddressForProperty}
    />
  );
};

export default SearchNow;
